<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import CustomParameterEditor from "./CustomParameterEditor.vue";
import ParameterSetTemplateTools from "./ParameterSetTemplateTools.vue";
import { getAdminCustomizationConfig } from "../api/products";
import { applyParameterSetTemplate, getParameterSetApplication, getParameterTemplateSource, type ParameterSetTemplate, type TemplateApplyRequest } from "../api/parameter-set-templates";
import { groupsFromSnapshot, parameterHash, pendingRequest, snapshotFromGroups } from "./custom-parameters/template-snapshot";
import { ApiClientError } from "../api/client";
import { groupsFromPresets } from "./custom-parameters/model";
import { copyDraft, mapGroups, mapPresets, ParameterSaveError, saveParameterDraft, type ImageDraft } from "./custom-parameters/binding";
import type { AdminCustomizationConfig, CustomOptionGroup, CustomOptionPreset } from "../api/products";

const props = defineProps<{ productId: string; config: AdminCustomizationConfig | null; presets: CustomOptionPreset[]; loading: boolean; disabled?: boolean; loadError?: string }>();
const emit = defineEmits<{ saved: [config: AdminCustomizationConfig]; reload: [] }>();
const baseline = ref<CustomOptionGroup[]>([]), draft = ref<CustomOptionGroup[]>([]), images = ref<ImageDraft[]>([]);
const mappedPublic = computed(() => mapPresets(props.presets));
const catalog = computed(() => groupsFromPresets(mappedPublic.value.presets, props.productId));
const issues = ref(0), revision = ref(0), busy = ref(false), blocked = ref(false), message = ref("");
const dirty = computed(() => JSON.stringify(draft.value) !== JSON.stringify(baseline.value) || images.value.length > 0);
const counts = computed(() => ({ publicGroups: mappedPublic.value.presets.length, publicValues: mappedPublic.value.presets.reduce((sum, group) => sum + group.values.length, 0),
  groups: baseline.value.length, values: baseline.value.reduce((sum, group) => sum + group.values.length, 0) }));
const templateTools = ref<InstanceType<typeof ParameterSetTemplateTools> | null>(null);
const imported = ref<{ templateId: string; templateHash: string; targetRevision: string; name: string } | null>(null);
const pendingApplication = ref<TemplateApplyRequest | null>(null), recoveryError = ref(false);
let generation = 0, disposed = false;
onBeforeUnmount(() => { disposed = true; generation++; });
watch(() => props.productId, () => {
  generation++; busy.value = false; blocked.value = false; message.value = ""; baseline.value = []; draft.value = []; images.value = []; imported.value = null; pendingApplication.value = null; recoveryError.value = false;
  try { pendingApplication.value = pendingRequest<TemplateApplyRequest>("apply", props.productId).read(); }
  catch { recoveryError.value = true; message.value = "上次导入记录无法读取，请保留页面联系管理员"; }
}, { immediate: true });
const canApplyTemplate = computed(() => !!props.config && props.config.product.id === props.productId && baseline.value.length === 0 && !dirty.value
  && ["DRAFT", "OFF_SALE"].includes(props.config.product.status) && !pendingApplication.value && !recoveryError.value);
function load(config: AdminCustomizationConfig) {
  const mapped = mapGroups(config.optionGroups, `product:${props.productId}`, config.optionValues);
  baseline.value = copyDraft(mapped.groups); draft.value = copyDraft(mapped.groups); issues.value = mapped.issues.length;
  images.value = []; blocked.value = false; imported.value = null; message.value = recoveryError.value ? "上次导入记录无法读取，请保留页面联系管理员" : pendingApplication.value ? "上次导入结果尚未核对，请点击核对导入结果；不会自动重试。" : ""; revision.value++;
}
watch(() => props.config, config => {
  if (!config || config.product.id !== props.productId) return;
  if (dirty.value && !busy.value) { message.value = "收到新的服务器配置，当前草稿已保留。保存前会再次核对版本。"; return; }
  load(config);
}, { immediate: true });
function canLeave() {
  if (busy.value || pendingApplication.value || recoveryError.value || !(templateTools.value?.canLeave() ?? true)) {
    message.value = "请先核对尚未确认的模板操作结果，再切换商品。";
    return false;
  }
  return !dirty.value || window.confirm("定制参数还有未保存的修改，确定放弃这些修改？");
}
function reload() {
  if (pendingApplication.value) { void verifyApplication(); return; }
  if (busy.value || !canLeave()) return;
  draft.value = copyDraft(baseline.value); images.value = []; emit("reload");
}
function cancel() { if (canLeave() && props.config) load(props.config); }
async function importTemplate(template: ParameterSetTemplate) {
  if (!canApplyTemplate.value || busy.value || props.disabled || props.loading) return;
  const version = ++generation, productId = props.productId;
  busy.value = true; message.value = "";
  try {
    const source = await getParameterTemplateSource(productId);
    if (disposed || version !== generation || productId !== props.productId) return;
    if (source.groupCount || !["DRAFT", "OFF_SALE"].includes(source.status)) throw new Error("当前商品已有参数或已上架，不能应用模板，请重新读取");
    draft.value = groupsFromSnapshot(template.snapshot, productId);
    imported.value = { templateId: template.id, templateHash: template.snapshotHash, targetRevision: source.revision, name: template.name };
    revision.value++; message.value = `已带入“${template.name}”，可以调整装订方式等选项，保存后才生效。`;
  } catch (cause) { if (version === generation) message.value = cause instanceof Error ? cause.message : "模板带入失败"; }
  finally { if (version === generation) busy.value = false; }
}
async function finishApplication(input: TemplateApplyRequest, productId: string, version: number) {
  const result = await getParameterSetApplication(input.id);
  if (result.productId !== productId || result.templateId !== input.templateId || result.id !== input.id
    || result.requestHash !== await parameterHash({ productId, ...input }) || result.snapshotHash !== await parameterHash(input.snapshot)) throw new Error("导入结果与本次请求不一致，请保留操作记录联系管理员");
  const actual = await getAdminCustomizationConfig(productId), mapped = mapGroups(actual.optionGroups, "template-readback", actual.optionValues);
  if (mapped.issues.length || actual.product.id !== productId || mapped.groups.some(group => !group.isActive)
    || await parameterHash(snapshotFromGroups(mapped.groups)) !== result.snapshotHash
    || result.groups.some(group => !mapped.groups.some(item => item.id === group.id && item.code === group.code
      && group.values.every(value => item.values.some(candidate => candidate.id === value.id && candidate.code === value.code))))) {
    throw new Error("导入已提交，但当前商品读回与提交内容不一致；请停止修改并联系管理员核对");
  }
  if (disposed || version !== generation || productId !== props.productId) return;
  pendingRequest<TemplateApplyRequest>("apply", productId).clear(); pendingApplication.value = null;
  load(actual); emit("saved", actual);
  await nextTick();
  if (!disposed && version === generation && productId === props.productId) message.value = "整套参数已保存，并已从服务器读回核对。各商品独立维护。";
}
async function verifyApplication() {
  if (busy.value || !pendingApplication.value || props.disabled || recoveryError.value) return;
  const input = JSON.parse(JSON.stringify(pendingApplication.value)) as TemplateApplyRequest, productId = props.productId, version = ++generation;
  busy.value = true;
  try { await finishApplication(input, productId, version); }
  catch (cause) { if (version === generation) message.value = `${cause instanceof Error ? cause.message : "核对未完成"}。请稍后再次核对，不会自动重试保存。`; }
  finally { if (version === generation) busy.value = false; }
}
async function saveImported() {
  if (!imported.value) return;
  if (images.value.length) { message.value = "首次应用模板请先保存参数，再为新商品上传其他图片。当前图片未上传。"; return; }
  const productId = props.productId, version = ++generation;
  busy.value = true; message.value = "";
  try {
    const input: TemplateApplyRequest = { id: crypto.randomUUID(), templateId: imported.value.templateId, templateHash: imported.value.templateHash,
      targetRevision: imported.value.targetRevision, snapshot: snapshotFromGroups(copyDraft(draft.value)) };
    if (!input.snapshot.groups.length) throw new Error("至少需要一个有效参数，或放弃此次模板应用");
    pendingRequest<TemplateApplyRequest>("apply", productId).write(input); pendingApplication.value = input;
    try { await applyParameterSetTemplate(productId, input); }
    catch (cause) {
      if (cause instanceof ApiClientError && cause.status === 409 && cause.code === "PARAMETER_APPLICATION_ROLLED_BACK") {
        pendingRequest<TemplateApplyRequest>("apply", productId).clear();
        if (!disposed && version === generation && productId === props.productId) {
          pendingApplication.value = null; blocked.value = false;
          message.value = "本次导入已确认回滚，未保存任何参数。草稿已保留，可调整后手动保存，或放弃修改。";
        }
        return; // No automatic POST retry. Unknown outcomes still use read-only recovery.
      }
      if (cause instanceof ApiClientError && [400, 403, 404, 409, 422].includes(cause.status)) {
        pendingRequest<TemplateApplyRequest>("apply", productId).clear(); if (!disposed && version === generation) pendingApplication.value = null;
      }
      throw cause;
    }
    await finishApplication(input, productId, version);
  } catch (cause) {
    if (version === generation) {
      blocked.value = true; message.value = `${cause instanceof Error ? cause.message : "导入未完成"}。${pendingApplication.value ? "结果尚未核对，请点击核对导入结果，不要重复保存。" : "没有继续写入，请重新读取后核对。"}`;
    }
  } finally { if (version === generation) busy.value = false; }
}
async function save() {
  if (busy.value || blocked.value || props.disabled || props.loading || props.loadError || issues.value || mappedPublic.value.issues.length || pendingApplication.value || recoveryError.value) return;
  if (imported.value) { await saveImported(); return; }
  busy.value = true; message.value = "";
  try {
    const result = await saveParameterDraft(props.productId, copyDraft(baseline.value), copyDraft(draft.value), images.value);
    load(result); emit("saved", result); message.value = "定制参数已保存，并已从服务器读回核对";
  } catch (failure) {
    blocked.value = true;
    const partial = failure instanceof ParameterSaveError && failure.attempted;
    message.value = `${failure instanceof Error ? failure.message : "保存未完成"}。${partial ? "可能已有部分操作完成；已停止后续写入，不会自动重试。" : "未继续写入。"}请重新读取核对。`;
  } finally { busy.value = false; }
}
defineExpose({ canLeave, dirty, reload });
</script>

<template>
  <div class="bound-parameters">
    <p
      v-if="loadError"
      class="bound-message"
      role="alert"
    >
      {{ loadError }}
    </p>
    <p
      class="parameter-source-count"
      data-parameter-source-count
    >
      公共参数 {{ counts.publicGroups }} 组 / {{ counts.publicValues }} 项 · 当前商品 {{ counts.groups }} 组 / {{ counts.values }} 项（含停用记录）
    </p>
    <p
      v-if="issues || mappedPublic.issues.length"
      class="bound-message"
      role="alert"
    >
      {{ issues + mappedPublic.issues.length }} 条异常记录已隔离，原数据未修改。为避免遗漏，暂时禁止保存，请核对原配置。
    </p>
    <ParameterSetTemplateTools
      ref="templateTools"
      :key="productId"
      :product-id="productId"
      :disabled="disabled || loading || busy || blocked || !!loadError || !!issues || !!mappedPublic.issues.length || !!pendingApplication || recoveryError"
      :dirty="dirty"
      :can-apply="canApplyTemplate"
      :has-parameters="baseline.some(group => group.isActive)"
      @apply="importTemplate"
    />
    <p
      v-if="imported"
      role="status"
    >
      正在应用“{{ imported.name }}”。当前是新商品草稿，保存后独立生效；首次保存前请沿用模板现有图片。
    </p>
    <button
      v-if="pendingApplication"
      type="button"
      :disabled="busy || disabled || recoveryError"
      @click="verifyApplication"
    >
      核对导入结果
    </button>
    <CustomParameterEditor
      v-if="config"
      :key="productId + ':' + revision"
      v-model="draft"
      :public-parameters="catalog"
      :disabled="disabled || loading || busy || blocked || !!loadError || !!pendingApplication || recoveryError"
      :persistent-images="true"
      :dimension-settings="true"
      @image-draft="images = $event"
    />
    <p v-else>
      正在读取商品的完整定制参数…
    </p>
    <p
      v-if="message"
      class="bound-message"
      role="status"
    >
      {{ message }}
    </p>
    <div class="bound-actions">
      <button
        type="button"
        class="bound-save"
        :disabled="!dirty || disabled || loading || busy || blocked || !!loadError || !!issues || !!mappedPublic.issues.length || !!pendingApplication || recoveryError"
        @click="save"
      >
        {{ busy ? '正在保存并核对…' : '保存定制参数' }}
      </button>
      <button
        type="button"
        :disabled="busy || !dirty || !!pendingApplication || recoveryError"
        @click="cancel"
      >
        放弃修改
      </button>
      <button
        type="button"
        :disabled="busy || loading"
        @click="reload"
      >
        重新读取
      </button>
    </div>
  </div>
</template>

<style scoped>
.bound-parameters { color:#333; }
.parameter-source-count { margin:0 0 12px; color:#666; font-size:12px; }
.bound-actions { display:flex; flex-wrap:wrap; gap:8px; margin-top:16px; }
.bound-actions button { padding:8px 14px; font:inherit; border:1px solid #e5e5e5; border-radius:4px; color:#333; background:#fff; cursor:pointer; }
.bound-actions .bound-save { color:#fff; background:#000; border-color:#000; }
.bound-actions button:disabled { color:#666; background:#f5f5f5; cursor:not-allowed; border-color:#e5e5e5; }
.bound-actions button:focus-visible { outline:2px solid #000; outline-offset:2px; }
.bound-message { padding:10px; border:1px solid #e5e5e5; background:#f5f5f5; font-size:13px; }
</style>
